import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowUp,
  Mic,
  Square,
  Volume2,
  VolumeX,
  Menu,
  Plus,
  X,
  Trash2,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { traditionById, MOOD_BACKGROUNDS, TIME_BACKGROUNDS } from "../lib/constants";
import { speak, stopSpeaking, createRecognition, hasSR, hasTTS, unlockSpeech } from "../lib/speech";
import { detectMood } from "../lib/mood";
import { getTimeOfDay } from "../lib/constants";
import Logo from "../components/Logo";

function Shimmer() {
  return (
    <div className="wisdom-card p-5 space-y-2.5">
      <div className="h-3 w-[82%] rounded-full shimmer bg-white/5" />
      <div className="h-3 w-[68%] rounded-full shimmer bg-white/5" />
      <div className="h-3 w-[76%] rounded-full shimmer bg-white/5" />
    </div>
  );
}

function NarratorMessage({ m, trad, onReplay, speakingId, onStop }) {
  const sections = [
    { label: "I hear you", body: m.feel },
    { label: "A story", body: m.story },
    { label: "The teaching", body: m.wisdom },
  ].filter((s) => s.body);

  // Fallback: raw content if parsing failed
  if (sections.length === 0) {
    return (
      <div className="flex gap-2.5 mb-5">
        <div
          className="w-8 h-8 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/30 flex-shrink-0 mt-1"
          style={{ backgroundImage: `url(${trad.image})` }}
        />
        <div className="wisdom-card p-4 flex-1">
          <p className="font-ui text-[#f8ecd4] text-[15.5px] leading-[1.65] whitespace-pre-wrap">
            {m.content}
          </p>
        </div>
      </div>
    );
  }

  const speaking = speakingId === m.id;
  return (
    <div className="flex gap-2.5 mb-5 items-start">
      <div
        className="w-8 h-8 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/30 flex-shrink-0 mt-1"
        style={{ backgroundImage: `url(${trad.image})` }}
        aria-label={trad.name}
      />
      <div className="flex-1 min-w-0 space-y-2">
        {sections.map((s, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.15, duration: 0.45, ease: "easeOut" }}
            className="wisdom-card p-4"
          >
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#d4af7a] font-ui mb-1.5">
              {s.label}
            </p>
            {/* All FEEL/STORY/WISDOM body text uses Fraunces REGULAR for readability.
                Italic reserved for short quotes, taglines, wordmark only. */}
            <p
              className="font-ui text-[#f8ecd4] text-[15.5px] leading-[1.65] whitespace-pre-wrap"
            >
              {s.body}
            </p>
          </motion.div>
        ))}
        <button
          onClick={() => (speaking ? onStop() : onReplay(m))}
          className="inline-flex items-center gap-1.5 text-[11px] text-[#ac9470] hover:text-[#f8ecd4] font-ui pt-1 transition-colors"
          data-testid={`replay-${m.id}`}
        >
          {speaking ? (
            <>
              <VolumeX size={13} strokeWidth={1.75} /> Stop
            </>
          ) : (
            <>
              <Volume2 size={13} strokeWidth={1.75} /> Replay
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function Sidebar({ open, onClose, onSelect, onNew, current }) {
  const [list, setList] = useState([]);
  const refresh = () =>
    api.get("/conversations").then((r) => setList(r.data || [])).catch(() => {});
  useEffect(() => {
    if (open) refresh();
  }, [open]);

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/conversations/${id}`).catch(() => {});
    refresh();
    if (id === current) onNew();
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={onClose}
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 26, stiffness: 220 }}
            className="fixed top-0 right-0 bottom-0 z-50 w-[86%] max-w-sm glass border-l border-[#d4af7a]/20 flex flex-col"
            style={{ background: "rgba(15, 8, 22, 0.92)" }}
          >
            <div className="flex items-center justify-between p-5">
              <h3 className="font-ui font-semibold text-[#f8ecd4] text-[17px]">Conversations</h3>
              <button
                onClick={onClose}
                data-testid="sidebar-close"
                className="p-2 rounded-full hover:bg-white/5 text-[#ac9470]"
              >
                <X size={18} />
              </button>
            </div>
            <button
              onClick={() => {
                onNew();
                onClose();
              }}
              data-testid="new-conversation-btn"
              className="mx-5 mb-4 btn-outline-gold rounded-full py-3 flex items-center justify-center gap-2 text-sm"
            >
              <Plus size={15} strokeWidth={1.75} /> New conversation
            </button>
            <div className="flex-1 overflow-y-auto px-3 pb-5">
              {list.length === 0 && (
                <p className="text-center text-[#ac9470] text-sm font-ui py-8">
                  No conversations yet.
                </p>
              )}
              {list.map((c) => (
                <button
                  key={c.id}
                  onClick={() => {
                    onSelect(c.id);
                    onClose();
                  }}
                  className={`group w-full text-left rounded-xl px-3 py-3 transition-all hover:bg-white/5 flex items-center gap-2 ${
                    current === c.id ? "bg-white/5" : ""
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-ui font-semibold text-[#f8ecd4] text-[15px] truncate">
                      {c.title}
                    </p>
                    <p className="text-[11px] text-[#ac9470] font-ui mt-0.5">
                      {new Date(c.updated_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                      })}{" "}
                      · {c.message_count} {c.message_count === 1 ? "turn" : "turns"}
                    </p>
                  </div>
                  <span
                    onClick={(e) => del(c.id, e)}
                    className="opacity-0 group-hover:opacity-100 p-1.5 text-[#ac9470] hover:text-red-400"
                    data-testid={`delete-${c.id}`}
                  >
                    <Trash2 size={14} />
                  </span>
                </button>
              ))}
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}

export default function Chat() {
  const nav = useNavigate();
  const { id: routeId } = useParams();
  const { state } = useLocation();
  const { user } = useAuth();

  const [conversationId, setConversationId] = useState(routeId || null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [interim, setInterim] = useState("");
  const [speakingId, setSpeakingId] = useState(null);
  const [sideOpen, setSideOpen] = useState(false);

  // Mood-adaptive background
  const [mood, setMood] = useState(null); // null means "use time-of-day"
  const moodHistoryRef = useRef({ last: null, msgsSinceChange: 99 });

  // Voice: read settings, track autoplay unlock + toast
  const [voiceOn, setVoiceOn] = useState(() => {
    try {
      const raw = localStorage.getItem("narrava_settings");
      if (raw) return JSON.parse(raw).voiceOn !== false;
    } catch { /* ignore */ }
    return true;
  });
  const [voiceToast, setVoiceToast] = useState(false);
  const lastSpokenRef = useRef(null);

  const recRef = useRef(null);
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);
  const trad = traditionById(user?.tradition || "all");

  const activeBg = mood ? MOOD_BACKGROUNDS[mood] : TIME_BACKGROUNDS[getTimeOfDay()];

  // Load conversation
  useEffect(() => {
    if (routeId) {
      setConversationId(routeId);
      api
        .get(`/conversations/${routeId}`)
        .then((r) => setMessages(r.data.messages || []))
        .catch(() => nav("/chat", { replace: true }));
    } else {
      setConversationId(null);
      setMessages([]);
    }
  }, [routeId, nav]);

  // Starter from Home
  useEffect(() => {
    if (state?.starter && !routeId && messages.length === 0) {
      send(state.starter);
    }
     
  }, []);

  // autoscroll
  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [messages, sending]);

  // Auto-read: when a new narrator message arrives and voice is on, speak it.
  useEffect(() => {
    if (!voiceOn || !hasTTS || messages.length === 0) return;
    const last = messages[messages.length - 1];
    if (last.role !== "narrator") return;
    if (lastSpokenRef.current === last.id) return;
    lastSpokenRef.current = last.id;

    const text = [last.feel, last.story, last.wisdom].filter(Boolean).join(". ") || last.content;
    const t = setTimeout(() => {
      setSpeakingId(last.id);
      speak(text, { tradition: user?.tradition || "krishna", gender: "female" });
      // Detect blocked autoplay: if nothing starts within 600ms, show toast once
      setTimeout(() => {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
          setVoiceToast(true);
        }
      }, 600);
      setTimeout(() => setSpeakingId((s) => (s === last.id ? null : s)), (text.length / 12) * 1000);
    }, 400);
    return () => clearTimeout(t);
  }, [messages, voiceOn]);

  // Unlock TTS on first user interaction with the chat screen
  useEffect(() => {
    const handler = () => {
      unlockSpeech();
      setVoiceToast(false);
    };
    window.addEventListener("pointerdown", handler, { once: false, passive: true });
    window.addEventListener("keydown", handler, { once: false });
    return () => {
      window.removeEventListener("pointerdown", handler);
      window.removeEventListener("keydown", handler);
    };
  }, []);

  const toggleVoice = () => {
    setVoiceOn((on) => {
      const next = !on;
      try {
        const raw = localStorage.getItem("narrava_settings");
        const s = raw ? JSON.parse(raw) : {};
        localStorage.setItem("narrava_settings", JSON.stringify({ ...s, voiceOn: next }));
      } catch { /* ignore */ }
      if (!next) stopSpeaking();
      return next;
    });
  };

  const maybeShiftMood = (text) => {
    const detected = detectMood(text);
    const h = moodHistoryRef.current;
    h.msgsSinceChange += 1;
    if (!detected) return;
    // Only change if it's a new mood AND at least 3 user messages since last change
    if (detected !== h.last && h.msgsSinceChange >= 3) {
      h.last = detected;
      h.msgsSinceChange = 0;
      setMood(detected);
    } else if (h.last === null && detected) {
      // First detection — allow immediate shift
      h.last = detected;
      h.msgsSinceChange = 0;
      setMood(detected);
    }
  };

  const autoGrow = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 140) + "px";
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setInput("");
    setInterim("");
    setSending(true);
    maybeShiftMood(text);
    const tempId = `u-${Date.now()}`;
    setMessages((m) => [
      ...m,
      { id: tempId, role: "user", content: text, ts: new Date().toISOString() },
    ]);
    try {
      const { data } = await api.post("/chat/message", {
        conversation_id: conversationId,
        content: text,
        tradition: user?.tradition || "all",
      });
      setConversationId(data.id);
      setMessages(data.messages);
      if (!routeId) window.history.replaceState(null, "", `/chat/${data.id}`);
    } catch (e) {
      setMessages((m) => [
        ...m,
        {
          id: `err-${Date.now()}`,
          role: "narrator",
          content:
            "The current is quiet just now. Please try again in a moment.\n\n" +
            (e?.response?.data?.detail || ""),
          ts: new Date().toISOString(),
        },
      ]);
    } finally {
      setSending(false);
    }
  };

  const toggleMic = () => {
    if (!hasSR) {
      alert("Voice input isn't supported in this browser yet.");
      return;
    }
    if (listening) {
      recRef.current?.stop();
      return;
    }
    const rec = createRecognition({
      lang: "en-IN",
      onResult: ({ interim: i, final }) => {
        setInterim(i);
        if (final) setInput((v) => (v ? v + " " : "") + final);
      },
      onEnd: () => {
        setListening(false);
        setInterim("");
      },
      onError: () => {
        setListening(false);
        setInterim("");
      },
    });
    recRef.current = rec;
    rec.start();
    setListening(true);
  };

  const replay = (m) => {
    stopSpeaking();
    setSpeakingId(m.id);
    const text = [m.feel, m.story, m.wisdom].filter(Boolean).join(". ") || m.content;
    speak(text, { tradition: user?.tradition || "krishna", gender: "female" });
    setTimeout(() => setSpeakingId((s) => (s === m.id ? null : s)), (text.length / 12) * 1000);
  };

  const stop = () => {
    stopSpeaking();
    setSpeakingId(null);
  };

  const newConversation = () => {
    setConversationId(null);
    setMessages([]);
    setMood(null);
    moodHistoryRef.current = { last: null, msgsSinceChange: 99 };
    nav("/chat", { replace: true });
  };

  const starters = [
    "I feel lost and don't know my purpose",
    "I'm paralysed by fear of failure",
    "How do I find peace in chaos?",
  ];

  return (
    <div className="min-h-screen max-w-md mx-auto w-full flex flex-col relative">
      {/* Adaptive atmospheric background */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse at top, rgba(40,18,40,1) 0%, rgba(10,5,16,1) 75%)",
          backgroundImage: "url(/fallback-scene.svg)",
          backgroundSize: "cover",
        }}
        aria-hidden
      />
      <div
        className="scene-layer ken-burns z-0"
        style={{
          backgroundImage: `url(${activeBg})`,
          opacity: 1,
        }}
        aria-hidden
      />
      {/* Stronger overlay on chat so text reads comfortably */}
      <div
        className="fixed inset-0 z-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(10,5,16,0.55) 0%, rgba(10,5,16,0.7) 50%, rgba(10,5,16,0.88) 100%)",
        }}
        aria-hidden
      />

      {/* Header */}
      <header className="sticky top-0 z-20 flex items-center justify-between px-4 py-3 glass border-b border-[#d4af7a]/15">
        <button
          data-testid="chat-back"
          onClick={() => nav("/home")}
          className="p-2 rounded-full hover:bg-white/5 text-[#ac9470]"
        >
          <ArrowLeft size={18} strokeWidth={1.75} />
        </button>
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/30"
            style={{ backgroundImage: `url(${trad.image})` }}
          />
          <div className="text-center">
            <p className="font-ui font-semibold text-[#f8ecd4] text-[17px] leading-tight">
              {trad.name}
            </p>
            <p className="text-[9.5px] tracking-[0.3em] uppercase text-[#d4af7a] font-ui">
              Present · Listening
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            data-testid="voice-toggle"
            onClick={toggleVoice}
            aria-label={voiceOn ? "Turn voice off" : "Turn voice on"}
            className={`p-2 rounded-full transition-all ${
              voiceOn
                ? "text-[#eac88a] bg-[#eac88a]/10 voice-pulse"
                : "text-[#6b5a4a] hover:text-[#ac9470]"
            }`}
          >
            {voiceOn ? <Volume2 size={17} strokeWidth={1.75} /> : <VolumeX size={17} strokeWidth={1.75} />}
          </button>
          <button
            data-testid="chat-menu"
            onClick={() => setSideOpen(true)}
            className="p-2 rounded-full hover:bg-white/5 text-[#ac9470]"
          >
            <Menu size={18} strokeWidth={1.75} />
          </button>
        </div>
      </header>

      {voiceToast && (
        <div className="fixed top-16 left-1/2 -translate-x-1/2 z-50 glass rounded-full px-4 py-2 text-[13px] text-[#f8ecd4] font-ui shadow-2xl animate-in fade-in slide-in-from-top-2">
          Tap anywhere to enable voice for this session
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} className="relative z-10 flex-1 overflow-y-auto px-4 py-6 pb-4">
        {messages.length === 0 && !sending && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center text-center pt-8 gap-6"
          >
            <Logo size={64} />
            <div className="space-y-2 max-w-xs">
              <h2 className="font-ui font-semibold text-[22px] text-[#f8ecd4]">
                What's on your heart?
              </h2>
              <p className="text-[#ac9470] text-[14px] font-ui leading-[1.5]">
                Speak freely. {trad.name} listens without judgment.
              </p>
            </div>
            <div className="w-full space-y-2 pt-2">
              {starters.map((s, i) => (
                <button
                  key={i}
                  data-testid={`chat-starter-${i}`}
                  onClick={() => send(s)}
                  className="w-full glass rounded-full px-4 py-3 text-left font-ui text-[#f8ecd4]/85 text-[14px] hover:border-[#d4af7a]/45 transition-all"
                >
                  {s}
                </button>
              ))}
            </div>
          </motion.div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m) =>
            m.role === "user" ? (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25 }}
                className="flex justify-end mb-5"
              >
                <div className="user-bubble px-4 py-3 max-w-[82%] font-ui text-[15px] leading-relaxed">
                  {m.content}
                </div>
              </motion.div>
            ) : (
              <NarratorMessage
                key={m.id}
                m={m}
                trad={trad}
                onReplay={replay}
                speakingId={speakingId}
                onStop={stop}
              />
            )
          )}
        </AnimatePresence>

        {sending && (
          <div className="flex gap-2.5 mb-5 items-start">
            <div
              className="w-8 h-8 rounded-lg bg-cover bg-center ring-1 ring-[#d4af7a]/30 flex-shrink-0 mt-1 voice-pulse"
              style={{ backgroundImage: `url(${trad.image})` }}
            />
            <div className="flex-1 space-y-2">
              <Shimmer />
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <div className="relative z-10 sticky bottom-0 px-4 pt-2 pb-[max(12px,env(safe-area-inset-bottom))] bg-gradient-to-t from-[#0a0510] via-[#0a0510]/90 to-transparent">
        {interim && (
          <div className="flex justify-end mb-2 pr-1">
            <div className="text-[12px] px-3 py-1.5 rounded-full border border-dashed border-[#d4af7a]/50 text-[#d4af7a] font-ui">
              {interim}
            </div>
          </div>
        )}
        <div className="glass rounded-[28px] px-2 py-2 flex items-end gap-1.5 shadow-[0_-8px_30px_rgba(0,0,0,0.4)]">
          <button
            data-testid="mic-btn"
            onClick={toggleMic}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              listening
                ? "bg-gradient-to-br from-[#eac88a] to-[#a07544] text-[#1a0d1d] voice-pulse"
                : "text-[#d4af7a] hover:bg-white/5"
            }`}
            aria-label={listening ? "Stop recording" : "Start voice input"}
          >
            {listening ? <Square size={15} fill="currentColor" /> : <Mic size={17} strokeWidth={1.75} />}
          </button>
          <textarea
            ref={textareaRef}
            data-testid="chat-input"
            className="flex-1 bg-transparent resize-none text-[#f8ecd4] font-ui text-[15px] py-2 px-2 placeholder:text-[#ac9470]/60 focus:outline-none leading-relaxed"
            rows={1}
            placeholder={`Ask ${trad.name} anything…`}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              autoGrow();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            style={{ maxHeight: 140 }}
          />
          <button
            data-testid="chat-send"
            onClick={() => send()}
            disabled={!input.trim() || sending}
            className="send-wave btn-gold w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 disabled:opacity-40 disabled:cursor-not-allowed text-[#1a0d1d]"
            aria-label="Send message"
          >
            <ArrowUp size={17} strokeWidth={2.25} />
          </button>
        </div>
      </div>

      <Sidebar
        open={sideOpen}
        onClose={() => setSideOpen(false)}
        onSelect={(id) => nav(`/chat/${id}`)}
        onNew={newConversation}
        current={conversationId}
      />
    </div>
  );
}
